import { IconAdjustments, IconTicket, IconPlus } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { Button, Text } from '@fragments';

import GlobeBackground from './components/GlobeBackground';
import FiltersSection from './components/FiltersSection';
import RevenueHero from './components/RevenueHero';
import MetricsSection from './components/MetricsSection';
import SalesSplitCard from './components/SalesSplitCard';
import SalesAmountChart from './components/SalesAmountChart';
import ProductRevenueChart from './components/ProductRevenueChart';

import './styles.scss';

export default function InsightsView(props) {
    const { t } = useTranslation('dashboard');

    const {
        ownedCommunities,
        communitiesLoading,
        community,
        plans,
        plansLoading,
        report,
        previousReport,
        reportLoading,
        selectedCurrency,
    } = props;

    if (!communitiesLoading && !ownedCommunities.length) {
        return (
            <div className="insights-page">
                <GlobeBackground />
                <NoCommunitiesState />
            </div>
        );
    }

    const showInsights = community && !plansLoading && plans.length > 0;
    const showNoPlans = community && !plansLoading && plans.length === 0;

    // The per-currency bucket the currency toggle points at (null while a currency
    // with no charges is selected — the charts/metrics render their empty state).
    const selectedReport = report?.currencyReports?.find(r => r.currency === selectedCurrency) || null;

    return (
        <div className="insights-page">
            <GlobeBackground />

            <div className="insights-page__filters-wrap mb-4 mt-6">
                <Text.h4
                    contrast={7}
                    left={<IconAdjustments/>}
                    className="mb-1"
                >
                    {t('insights.filtersTitle')}
                </Text.h4>
                <FiltersSection {...props} />
            </div>

            {showInsights && (
                <>
                    {/* Lead row: the gross revenue hero set straight on the canvas
                        (growth · figure · trend line), with the KPI tiles beside it. */}
                    <div className="insights-page__grid">
                        <RevenueHero
                            report={report}
                            previousReport={previousReport}
                            currency={selectedCurrency}
                            reportLoading={reportLoading}
                        />
                        <MetricsSection
                            report={report}
                            currency={selectedCurrency}
                            reportLoading={reportLoading}
                        />
                    </div>

                    {/* Charts key their skeleton/empty state off the top-level `report`
                        (loaded at all?) and read their data from the selected currency,
                        so an empty currency shows "sem dados", not a re-fetch skeleton. */}
                    <div className="insights-page__grid mt-3">
                        <ProductRevenueChart report={report} productRevenue={selectedReport?.productRevenue} currency={selectedCurrency} reportLoading={reportLoading} plans={plans} />
                        <SalesSplitCard report={report} reportLoading={reportLoading} />
                    </div>
                    <div className="mt-3">
                        <SalesAmountChart report={report} timeSeries={selectedReport?.salesAmount} currency={selectedCurrency} reportLoading={reportLoading} />
                    </div>
                </>
            )}

            {showNoPlans && (
                <NoPlansState
                    community={ownedCommunities.find(c => c.id === community)}
                />
            )}
        </div>
    );
}

function NoCommunitiesState() {
    const { t } = useTranslation('dashboard');

    return (
        <div className="insights-page__empty">
            <Text.h4 contrast={7}>
                {t('insights.empty.noCommunitiesTitle')}
            </Text.h4>
            <Text.p contrast={5} className="mt-1">
                {t('insights.empty.noCommunitiesSubtitle')}
            </Text.p>
        </div>
    );
}

function NoPlansState({ community }) {
    const { t } = useTranslation('dashboard');

    function handleCreatePlan() {
        if (!community?.slug) return;
        window.navigate(`/${community.slug}/settings/pricing`);
    }

    return (
        <div className="insights-page__empty">
            <IconTicket className='csf-2 mb-1' size={40}/>
            <Text.h4 contrast={7} weight={400}>
                {t('insights.empty.noPlansTitle')}
            </Text.h4>
            <Text.h5 contrast={5} className="mt-1">
                {t('insights.empty.noPlansSubtitle')}
            </Text.h5>
            <Button
                className="mt-3"
                size="medium"
                startIcon={<IconPlus size={16} stroke={2} />}
                text={t('insights.empty.createPlan')}
                color="inverse"
                onClick={handleCreatePlan}
            />
        </div>
    );
}
